import { Alert, Box, Card, IconButton, Snackbar, Tooltip, Typography, useTheme } from '@mui/material';
import { Add, DeleteOutlined, DrawOutlined, FileOpenOutlined, MoveDownOutlined, Remove } from '@mui/icons-material';
import { GetWarehouseActionsRequest, GetWarehouseDataDetailResponse, GetWarehousesPermissionsResponse, WarehouseActionDTO, WarehouseTabType, WarehouseVovClient } from '../../../ApiOld/Api/ApiServerVov';
import React, { useEffect, useState } from 'react';
import { useApi } from '../../../ApiOld/Api/useApi';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import WarehouseSignatureDialog from './Dialogs/WarehouseSignatureDialog';
import WarehouseActionsGrid from './WarehouseActionsGrid';

interface WarehouseActionsProps {
  warehouseId: number | undefined;
  warehouseData?: Partial<GetWarehouseDataDetailResponse> | null;
  tabType: WarehouseTabType;
  permissions?: GetWarehousesPermissionsResponse | null;
  setWarehouseData?: (data: Partial<GetWarehouseDataDetailResponse> | null) => void;
}

export default function WarehouseActions(props: Readonly<WarehouseActionsProps>) {
  const { warehouseId, warehouseData, tabType, permissions, setWarehouseData } = props;
  const { authorizedApi } = useApi(WarehouseVovClient);
  const [warehouseAction, setWarehouseAction] = useState<WarehouseActionDTO[] | undefined>();
  const [expandedActions, setExpandedActions] = useState<Set<number>>(new Set());
  const [openSignature, setOpenSignature] = React.useState(false);
  const [stockInOutListSign, setStockInOutListSign] = React.useState(false);
  const [error, setError] = useState('');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const theme = useTheme();
  const inWaitingForSignatureIdRef = React.useRef<number | undefined>(undefined);

  useEffect(() => {
    refresh();
  }, [authorizedApi, warehouseId, tabType, params]);

  const refresh = () => {
    if (warehouseId) {
      authorizedApi
        .getWarehouseActions(new GetWarehouseActionsRequest({ lang: params['lang'] ?? '', warehouseId: warehouseId, tabType: tabType }))
        .then((r) => {
          setStockInOutListSign(r.stockInOutListSign ?? false);
          setWarehouseAction(r ? r.warehouseActions : []);
          if (tabType === WarehouseTabType.OpenActions && setWarehouseData)
            setWarehouseData({ ...warehouseData, openActionCount: (r.warehouseActions ?? []).length });
        });
    } else {
      setWarehouseAction([]);
    }
  }

  const toggleExpand = (actionId: number) => {
    setExpandedActions((prev) => {
      const updated = new Set(prev);
      if (updated.has(actionId)) {
        updated.delete(actionId);
      } else {
        updated.add(actionId);
      }
      return updated;
    });
  };

  const getStateName = (state: string) => {
    switch (state) {
      case 'draft_buy':
      case 'buy':
        return t('warehouses.purchase');
      case 'draft_block':
      case 'block':
        return t('warehouses.reservation');
      case 'draft_out':
      case 'out':
        return t('warehouses.disbursement');
      case 'in_waiting_for_signature':
        if (tabType === WarehouseTabType.OpenActions) {
          return t('warehouses.receiptWaitingForSignature');
        }
        else
          return t('warehouses.receipt');
      case 'in':
        return t('warehouses.receipt');
      case 'draft_utility':
      case 'utility':
        return t('warehouses.consumption');
      case 'draft_write_off':
      case 'write_off':
        return t('warehouses.writeOff');
      default:
        return 'Neznámy stav';
    }
  }

  const handleOpenActionClick = (id: number, state: string) => {
    if (permissions?.stockWarehousesEdit) {
      if (warehouseId) {
        switch (state) {
          case 'draft_buy':
            navigate('/' + params['lang'] + '/' + params['domain']! + '/userlayout/warehousePurchase/' + warehouseId + '/' + id);
            break;
          case 'draft_block':
            navigate('/' + params['lang'] + '/' + params['domain']! + '/userlayout/warehouseBlock/0/' + warehouseId + '/' + id);
            break;
          case 'draft_out':
            navigate('/' + params['lang'] + '/' + params['domain']! + '/userlayout/warehouseOut/0/' + warehouseId + '/' + id);
            break;
          case 'draft_utility':
            navigate('/' + params['lang'] + '/' + params['domain']! + '/userlayout/warehouseUtility/' + warehouseId + '/' + id);
            break;
          case 'draft_write_off':
            navigate('/' + params['lang'] + '/' + params['domain']! + '/userlayout/warehouseWriteOff/' + warehouseId + '/' + id);
            break;
          case 'in_waiting_for_signature':
            inWaitingForSignatureIdRef.current = id;
            setOpenSignature(true);
            break;
          default:
            break;
        }
      }
    }
  };

  const handleOpenTransfer = (id: number) => {
    authorizedApi.updateBlockToDraftOut(id).then(() => {
      navigate('/' + params['lang'] + '/' + params['domain']! + '/userlayout/warehouseOut/0/' + warehouseId + '/' + id);
    });
  };

  const handleDeleteReservation = (id: number) => {
    authorizedApi.deleteListWithItems(id).then(() => {
      setWarehouseAction(warehouseAction?.filter((x) => x.id !== id));
      if (setWarehouseData)
        setWarehouseData({ ...warehouseData, reservationCount: (warehouseAction?.filter((x) => x.id !== id) ?? []).length });
    });
  };

  const getOpenActionText = (state: string) => {
    switch (state) {
      case 'draft_buy':
        return t('warehouses.openPurchase');
      case 'draft_block':
        return t('warehouses.openReservation');
      case 'draft_out':
        return t('warehouses.openDisbursement');
      case 'draft_utility':
        return t('warehouses.openConsumption');
      case 'draft_write_off':
        return t('warehouses.openWriteOff');
      case 'in_waiting_for_signature':
        return t('warehouses.signatureReceipt');
      default:
        return '';
    }
  }

  return (
    <Box sx={{ maxWidth: '100vw' }}>
      <Card sx={{ height: '100%' }} elevation={3}>
        {warehouseAction?.map((action) => (
          <Box key={action.id}>
            <Box sx={{ borderBottom: '1px solid #ccc', '&:hover': { backgroundColor: '#eeeeee' } }} onClick={() => toggleExpand(action.id!)}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: (action.note ?? '').trim() !== '' ? 0 : 1 }}>
                <IconButton size="small" sx={{ mt: 1 }}>
                  {expandedActions.has(action.id!) ? <Remove fontSize="small" /> : <Add fontSize="small" />}
                </IconButton>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 2, cursor: tabType === WarehouseTabType.OpenActions ? 'pointer' : 'default' }} >
                  <Typography variant="body2">{action.datetime?.toLocaleDateString() + ' ' + action.datetime?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Typography>
                  <Typography variant="body2">{action.userName}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '350px' }}>
                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                      {getStateName(action.state ?? '') + ((tabType === WarehouseTabType.OpenActions && ((action.state ?? '') !== 'in_waiting_for_signature')) ? '' : ':')}
                    </Typography>
                    {tabType === WarehouseTabType.OpenActions && ((action.state ?? '') !== 'in_waiting_for_signature') && (
                      <Typography variant="body2" style={{ marginLeft: '10px' }}>
                        {((((action.state ?? '') === 'out') || ((action.state ?? '') === 'draft_out') || ((action.state ?? '') === 'block') || ((action.state ?? '') === 'draft_block')) ? action.toWarehouseName : '')}
                      </Typography>
                    )}
                    {(tabType === WarehouseTabType.MovementsInStock || (tabType === WarehouseTabType.OpenActions && ((action.state ?? '') === 'in_waiting_for_signature'))) && (
                      <Typography variant="body2" style={{ marginLeft: '2px' }}>
                        {(action.number ?? '').trim() + ((((action.state ?? '') === 'out') || ((action.state ?? '') === 'draft_out') || ((action.state ?? '') === 'block') || ((action.state ?? '') === 'draft_block')) ? (' / ' + action.toWarehouseName) : '') + ((((action.state ?? '') === 'in') || ((action.state ?? '') === 'in_waiting_for_signature')) ? (' / ' + action.fromWarehouseName) : '')}
                      </Typography>
                    )}
                    {tabType === WarehouseTabType.Reservations && (
                      <Typography variant="body2" style={{ marginLeft: '2px' }}>
                        {action.number + ' / ' + action.toWarehouseName}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {tabType === WarehouseTabType.OpenActions && (
                      <Tooltip title={getOpenActionText(action.state ?? '')}>
                        <IconButton
                          size='small'
                          disabled={!permissions?.stockWarehousesEdit}
                          sx={{ marginLeft: '100px', color: theme.palette.primary.dark }}
                          onClick={() => handleOpenActionClick(action.id!, action.state!)}
                        >
                          {(action.state ?? '') === 'in_waiting_for_signature' ? <DrawOutlined /> : <FileOpenOutlined />}
                        </IconButton>
                      </Tooltip>
                    )}
                    {tabType === WarehouseTabType.Reservations && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Tooltip title={t('warehouses.openTransfer')}>
                          <IconButton
                            size='small'
                            disabled={!permissions?.stockWarehousesEdit || !permissions?.stockListsStockPermBlock}
                            sx={{ marginLeft: '100px', color: theme.palette.primary.dark }}
                            onClick={() => handleOpenTransfer(action.id!)}
                          >
                            <MoveDownOutlined />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('warehouses.deleteReservation')}>
                          <IconButton
                            size='small'
                            disabled={!permissions?.stockWarehousesEdit || !permissions?.stockListsStockPermBlock}
                            sx={{ marginLeft: '20px', color: theme.palette.primary.dark }}
                            onClick={() => handleDeleteReservation(action.id!)}
                          >
                            <DeleteOutlined />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
              {(action.note ?? '').trim() !== '' && <Typography variant="body2" sx={{ marginLeft: '30px', mb: 1 }}>
                {(action.note ?? '').trim()}
              </Typography>}
            </Box>
            {expandedActions.has(action.id!) && (
              <WarehouseActionsGrid rows={action.stockItems ?? []} />
            )}
          </Box>
        ))}
      </Card>
      <WarehouseSignatureDialog
        open={openSignature}
        stockInOutListSign={stockInOutListSign}
        onClose={(file, closeTransaction) => {
          setOpenSignature(false);
          if (closeTransaction) {
            if (!stockInOutListSign || file) {
              authorizedApi.updateInWaitingForSignatureToIn(inWaitingForSignatureIdRef.current!).then((r) => {
                if (r.ok) {
                  if (file)
                    authorizedApi.updateStockListSignature(inWaitingForSignatureIdRef.current, true, file);
                  refresh();
                }
              });
            }
            else
              setError(t('warehouses.signRequired'));
          }
        }}
      />
      <Snackbar open={(error ?? '') !== ''} autoHideDuration={5000} onClose={() => setError('')}>
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}
